import { useState, useEffect } from 'react';

interface BusinessHoursConfig {
  start: number;
  end: number;
  timezone: string;
  weekdays: number[];
}

const DEFAULT_CONFIG: BusinessHoursConfig = {
  start: 9,
  end: 21,
  timezone: 'America/New_York',
  weekdays: [1, 2, 3, 4, 5],
};

function getHourInTimezone(date: Date, timezone: string): number {
  return parseInt(
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      hour12: false,
    }).format(date)
  );
}

function getDayInTimezone(date: Date, timezone: string): number {
  const dayStr = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
  }).format(date);
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return dayMap[dayStr] ?? 0;
}

function findNextValidSlot(startFrom: Date, config: BusinessHoursConfig): Date {
  const candidate = new Date(startFrom);
  if (candidate.getMinutes() > 0 || candidate.getSeconds() > 0) {
    candidate.setHours(candidate.getHours() + 1, 0, 0, 0);
  }

  for (let attempts = 0; attempts < 14 * 24; attempts++) {
    const hour = getHourInTimezone(candidate, config.timezone);
    const day = getDayInTimezone(candidate, config.timezone);

    if (config.weekdays.includes(day) && hour >= config.start && hour < config.end) {
      return candidate;
    }

    if (hour >= config.end || !config.weekdays.includes(day)) {
      candidate.setHours(candidate.getHours() + (24 - hour + config.start), 0, 0, 0);
    } else if (hour < config.start) {
      candidate.setHours(candidate.getHours() + (config.start - hour), 0, 0, 0);
    } else {
      candidate.setHours(candidate.getHours() + 1, 0, 0, 0);
    }
  }

  return candidate;
}

export function useWebinarTimes() {
  const [webinarTimes, setWebinarTimes] = useState<Date[]>([]);
  const [nextWebinarTime, setNextWebinarTime] = useState<Date>(new Date());
  const [businessHours, setBusinessHours] = useState<BusinessHoursConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    fetch('/api/webinar/business-hours')
      .then(res => res.json())
      .then((data: BusinessHoursConfig) => {
        if (data && data.start !== undefined && data.end !== undefined) {
          setBusinessHours(data);
        }
      })
      .catch(() => {
        // Use defaults on failure — already set
      });
  }, []);

  useEffect(() => {
    const calculateWebinarTimes = () => {
      const now = new Date();

      const slot1Start = new Date(now.getTime() + 60 * 60 * 1000);
      const time1 = findNextValidSlot(slot1Start, businessHours);

      const slot2Start = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const time2 = findNextValidSlot(slot2Start, businessHours);

      const slot3Start = new Date(now.getTime() + 48 * 60 * 60 * 1000);
      const time3 = findNextValidSlot(slot3Start, businessHours);

      const times = [time1, time2, time3];
      setWebinarTimes(times);

      const nextTime = times.reduce((earliest, current) =>
        current < earliest ? current : earliest, times[0]);
      setNextWebinarTime(nextTime);
    };

    calculateWebinarTimes();

    const intervalId = setInterval(calculateWebinarTimes, 60000);
    return () => clearInterval(intervalId);
  }, [businessHours]);

  return { webinarTimes, nextWebinarTime };
}
